import { useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, ClipboardPaste, Loader2, Send } from "lucide-react";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TipsaSida() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  usePageMeta({
    title: "Tipsa VIDÄLV – dela något du sett | vidalv.live",
    description: "Såg du något som fler i bygden borde känna till? Klistra in texten eller länken här så går den till VIDÄLV:s granskning.",
    path: "/tipsa",
  });

  const submit = async (value = text) => {
    const clean = value.trim();
    if (!clean) {
      setError("Klistra in texten eller länken först.");
      textareaRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/tips`, { text: clean });
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 429) {
        setError("Du har skickat flera tips på kort tid. Vänta en stund och försök igen.");
      } else {
        setError(err?.response?.data?.detail || "Något gick fel – försök igen.");
      }
    } finally {
      setBusy(false);
    }
  };

  const pasteAndSubmit = async () => {
    try {
      const clip = (await navigator.clipboard.readText())?.trim();
      if (!clip) throw new Error("empty");
      setText(clip);
      await submit(clip);
    } catch {
      setError("Kunde inte läsa urklippet automatiskt. Klistra in i rutan nedan.");
      textareaRef.current?.focus();
    }
  };

  if (done) {
    return (
      <div data-testid="tipsa-page" className="max-w-xl mx-auto px-4 sm:px-6 py-14">
        <div className="bg-parchment border border-pine/40 rounded-sm p-8 text-center">
          <CheckCircle2 size={44} className="text-pine mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="font-heading text-xl sm:text-2xl text-ink mb-2">Tack för ditt tips!</h1>
          <p className="text-sm text-bark leading-relaxed mb-6">Tipset är mottaget och ligger i VIDÄLV:s granskningskö. Inget publiceras eller tas bort automatiskt från den här sidan.</p>
          <button type="button" onClick={() => { setDone(false); setText(""); }} className="border border-pine text-pine px-5 py-2.5 rounded-sm text-sm font-semibold">Tipsa om något mer</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="tipsa-page" className="max-w-xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Dela med bygden</p>
      <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-3">Tipsa VIDÄLV</h1>
      <p className="text-base text-ink leading-relaxed mb-6">Såg du ett evenemang, ett meddelande eller något annat som kan vara värdefullt för bygden? <strong>Kopiera texten eller länken och lämna den här.</strong></p>

      <button type="button" onClick={pasteAndSubmit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2.5 bg-pine text-cream font-bold text-base px-6 py-4 rounded-sm disabled:opacity-60 mb-4">
        {busy ? <Loader2 size={20} className="animate-spin" /> : <ClipboardPaste size={20} />}
        {busy ? "Skickar…" : "Klistra in och skicka"}
      </button>

      <p className="text-xs text-bark/70 text-center mb-4">…eller klistra in själv:</p>
      <textarea ref={textareaRef} value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder="Klistra in text eller länk…" className="w-full bg-white border border-warm-border rounded-sm p-4 text-sm text-ink mb-3" />
      {error && <p className="text-sm text-falu bg-falu/5 border border-falu/30 rounded-sm px-4 py-2.5 mb-3">{String(error)}</p>}
      <button type="button" onClick={() => submit()} disabled={busy || !text.trim()} className="w-full inline-flex items-center justify-center gap-2 border border-pine text-pine font-semibold text-sm px-6 py-3 rounded-sm disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Skicka till granskning
      </button>
      <p className="text-xs text-bark/60 leading-relaxed mt-6">VIDÄLV kan klassificera tipset automatiskt för att hjälpa redaktionen, men tipset går först till granskning. Den här sidan publicerar eller avbokar inget på egen hand.</p>
    </div>
  );
}
